import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import {
    Instagram,
    Mail,
    MapPin,
    MessageCircle,
    MessageSquare,
    Phone,
    Send
} from 'lucide-react'
import { BrandLogo } from '@/components/generals/brand-mark'
import { contactInfo, mainNav, siteConfig } from '@/constants/site'

type SocialLink = {
    label: string
    href: string
    icon: LucideIcon
}

const socialLinks: SocialLink[] = [
    { label: "اینستاگرام", href: contactInfo.instagram.url, icon: Instagram },
    { label: "تلگرام", href: contactInfo.telegram.url, icon: Send },
    { label: "واتس‌اپ", href: contactInfo.whatsapp.url, icon: MessageCircle },
    { label: "ایتا", href: contactInfo.eitaa.url, icon: MessageSquare }
]

const serviceLinks = [
    { label: "خرید عمده و صادرات", href: '/export' },
    { label: "آنالیز آزمایشگاهی", href: '/process#lab' },
    { label: "بسته‌بندی بهداشتی", href: '/process#packaging' },
    { label: "درخواست نمونه و پیش‌فاکتور", href: '/contact' }
]

const persianYear = (): string => {
    try {
        return new Intl.DateTimeFormat('fa-IR-u-ca-persian', { year: 'numeric' }).format(new Date())
    }
    catch {
        return ''
    }
}

export const Footer: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-forest-950 text-cream'>
            <div aria-hidden='true' className='bg-girih pointer-events-none absolute inset-0 opacity-[0.05]' />
            <div aria-hidden='true' className='pointer-events-none absolute inset-x-0 top-0 h-px bg-linear-to-l from-transparent via-gold/50 to-transparent' />

            <div className='relative mx-auto max-w-7xl px-5 pb-10 pt-16 sm:px-8 lg:pt-20'>
                <div className='grid gap-12 sm:grid-cols-2 lg:grid-cols-12'>
                    <div className='sm:col-span-2 lg:col-span-4'>
                        <BrandLogo />

                        <p className='mt-6 max-w-sm text-sm leading-7 text-cream/70'>
                            {siteConfig.description}
                        </p>

                        <ul className='mt-6 flex flex-wrap gap-2.5' aria-label='شبکه‌های اجتماعی'>
                            {socialLinks.map(({ label, href, icon: Icon }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={label}
                                        title={label}
                                        className='inline-flex size-11 items-center justify-center rounded-full border border-cream/15 text-cream/80 transition-colors hover:border-gold hover:text-gold'
                                    >
                                        <Icon aria-hidden='true' className='size-[1.15rem]' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <nav aria-labelledby='footer-quick-links' className='lg:col-span-2'>
                        <h2 id='footer-quick-links' className='text-xl font-bold text-gold'>
                            دسترسی سریع
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm'>
                            {mainNav.map(item => (
                                <li key={item.href}>
                                    <Link href={item.href} className='text-cream/75 transition-colors hover:text-gold'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-labelledby='footer-services' className='lg:col-span-3'>
                        <h2 id='footer-services' className='text-xl font-bold text-gold'>
                            خدمات
                        </h2>

                        <ul className='mt-5 space-y-3 text-sm'>
                            {serviceLinks.map(item => (
                                <li key={item.label}>
                                    <Link href={item.href} className='text-cream/75 transition-colors hover:text-gold'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className='sm:col-span-2 lg:col-span-3'>
                        <h2 className='text-xl font-bold text-gold'>
                            تماس با ما
                        </h2>

                        <ul className='mt-5 space-y-4 text-sm'>
                            {contactInfo.phones.map(phone => (
                                <li key={phone.tel} className='flex items-start gap-3'>
                                    <Phone aria-hidden='true' className='mt-1 size-4 shrink-0 text-gold' />

                                    <span className='flex flex-col'>
                                        <span className='text-cream/60'>{phone.name}</span>
                                        <a href={`tel:${phone.tel}`} dir='ltr' className='self-start text-cream transition-colors hover:text-gold'>
                                            {phone.display}
                                        </a>
                                    </span>
                                </li>
                            ))}

                            <li className='flex items-start gap-3'>
                                <Mail aria-hidden='true' className='mt-1 size-4 shrink-0 text-gold' />
                                <a href={`mailto:${contactInfo.email}`} dir='ltr' className='break-all text-cream transition-colors hover:text-gold'>
                                    {contactInfo.email}
                                </a>
                            </li>

                            <li className='flex items-start gap-3'>
                                <MapPin aria-hidden='true' className='mt-1 size-4 shrink-0 text-gold' />
                                <span className='text-cream/80'>{contactInfo.address}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className='mt-14 flex flex-col gap-3 border-t border-cream/10 pt-6 text-xs text-cream/55 sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        © <span suppressHydrationWarning>{persianYear()}</span> {siteConfig.name}. همه حقوق محفوظ است.
                    </p>

                    <p>{siteConfig.tagline}</p>
                </div>
            </div>
        </footer>
    )
}
